import { IntlProvider, loadMessages, LocalizationProvider } from '@progress/kendo-react-intl';
import { GlobalizationContext } from './GlobalizationContext';
import React, { ReactNode, useCallback, useState } from 'react';
import { GEAR_LOCALE, GearLocale } from '../../Model/GearLocale';
import { DEFAULT_LOCALE, LOCALE_MAPPING, USER_LOCALE_SETTINGS_KEY } from '../../config/consts';
import ServicesContext from '../../global/services/ServicesContext';
import { IdNamePair } from '../../Model/IdNamePair';
import { useGetTranslations } from '../translations/useGetTranslations';

interface GlobalizationProviderProps {
  children: ReactNode;
}

export interface GearLanguage {
  locale: GearLocale;
  text: string;
  icon?: string;
}

const getLanguageFromLocale = (locale: GearLocale) => {
  return locale.split(/[_-]/)[0];
};

const setLocaleSettingsKey = (locale: string) => {
  window.localStorage.setItem(USER_LOCALE_SETTINGS_KEY, locale);
};

const getLocaleSettingsKey = () => {
  return window.localStorage.getItem(USER_LOCALE_SETTINGS_KEY);
};

const getSupportedLocale = (localLocale: string) => {
  if (Object.values(GEAR_LOCALE).includes(localLocale as GearLocale)) {
    return localLocale as GearLocale;
  } else {
    return DEFAULT_LOCALE;
  }
};

const getUserLocale = () => {
  const storedLocale = getLocaleSettingsKey();
  if (!storedLocale) {
    const browserLocale = navigator.language;
    return getSupportedLocale(browserLocale);
  }
  return getSupportedLocale(storedLocale);
};

const GlobalizationProvider: React.FC<GlobalizationProviderProps> = props => {
  const [selectedLocale, setSelectedLocale] = useState<GearLocale>(DEFAULT_LOCALE);
  const [availableLocales, setAvailableLocales] = useState<IdNamePair[] | undefined>(undefined);
  const servicesCtx = React.useContext(ServicesContext);

  const { data: translations } = useGetTranslations(selectedLocale);

  React.useEffect(() => {
    if (selectedLocale && translations) {
      const kendoLocale = LOCALE_MAPPING[selectedLocale as keyof typeof LOCALE_MAPPING];
      if (kendoLocale) {
        loadMessages(translations, kendoLocale);
      }
    }
  }, [selectedLocale, translations]);

  React.useEffect(() => {
    const fetchLocales = async () => {
      try {
        if (servicesCtx?.localesService) {
          const locales = await servicesCtx.localesService.getLocales();
          setAvailableLocales(locales);
        }
      } catch {
        // empty
      }
    };

    const userLocale = getUserLocale();
    setSelectedLocale(userLocale);
    fetchLocales();
  }, [servicesCtx]);

  const handleSetLocale = useCallback((newLocale: GearLocale) => {
    const userLocale = getSupportedLocale(newLocale);
    setLocaleSettingsKey(userLocale);
    setSelectedLocale(userLocale);
  }, []);

  if (!availableLocales) {
    return null;
  }

  return (
    <GlobalizationContext.Provider
      value={{
        availableLocales,
        selectedLocale,
        setSelectedLocale: handleSetLocale
      }}
    >
      <IntlProvider locale={selectedLocale}>
        <LocalizationProvider language={getLanguageFromLocale(selectedLocale)}>{props.children}</LocalizationProvider>
      </IntlProvider>
    </GlobalizationContext.Provider>
  );
};

export default GlobalizationProvider;
